import React from 'react';

interface SkeletonProps {
  className?: string;
}

/** A single shimmering block. Always render inside an `aria-busy` region. */
export const Skeleton: React.FC<SkeletonProps> = ({ className = 'h-4 w-full' }) => (
  <div className={`animate-shimmer rounded bg-surface-active ${className}`} />
);

interface SkeletonGroupProps {
  label: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Wraps a skeleton layout so screen readers hear "Loading orders" once rather
 * than nothing at all — the previous pattern was a bare spinning div with no
 * accessible name.
 */
export const SkeletonGroup: React.FC<SkeletonGroupProps> = ({ label, className = '', children }) => (
  <div aria-busy="true" aria-live="polite" className={className}>
    <span className="sr-only">Loading {label}</span>
    {children}
  </div>
);

/** Skeleton shaped like the KPI row it replaces, so the layout does not jump. */
export const MetricRowSkeleton: React.FC<{ count?: number; label: string }> = ({ count = 4, label }) => (
  <SkeletonGroup label={label} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="rounded-card border border-border-subtle bg-surface p-5 shadow-card">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="mt-3 h-8 w-20" />
        <Skeleton className="mt-3 h-3 w-28" />
      </div>
    ))}
  </SkeletonGroup>
);

/** Skeleton shaped like a data table. */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number; label: string }> = ({
  rows = 5,
  columns = 5,
  label,
}) => (
  <SkeletonGroup label={label} className="overflow-hidden rounded-card border border-border-subtle bg-surface shadow-card">
    <div className="border-b border-border-subtle bg-surface-sunken px-5 py-3">
      <Skeleton className="h-3.5 w-32" />
    </div>
    <div className="divide-y divide-border-subtle">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-5 py-4">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton key={columnIndex} className={`h-3.5 ${columnIndex === 0 ? 'w-40' : 'w-20'}`} />
          ))}
        </div>
      ))}
    </div>
  </SkeletonGroup>
);

export default Skeleton;
